<script setup lang="ts">
import {ref, watch} from 'vue'

const props = withDefaults(
  defineProps<{
    text?: string
    duration?: number
    debounceMs?: number
  }>(),
  {
    duration: 1000,
    debounceMs: 150,
  }
)

let debounceTimer: number | null = null
let pendingText: string | undefined = undefined
let hasPendingText = false

const isTransitioning = ref(false)
const displayedText = ref(props.text)

watch(
  () => props.text,
  (newText) => {
    if (debounceTimer) window.clearTimeout(debounceTimer)
    debounceTimer = window.setTimeout(() => {
      updateText(newText)
    }, props.debounceMs)
  }
)

function updateText(newText: string | undefined) {
  if (isTransitioning.value) {
    pendingText = newText
    hasPendingText = true
    return
  }

  if (newText === displayedText.value) return

  isTransitioning.value = true
  displayedText.value = newText
}

function handleAfterEnter() {
  isTransitioning.value = false
  if (hasPendingText) {
    const nextText = pendingText
    pendingText = undefined
    hasPendingText = false
    updateText(nextText)
  }
}
</script>

<template>
  <div class="relative inline-flex items-center justify-center">
    <Transition mode="out-in" name="fade" @after-enter="handleAfterEnter">
      <span :key="displayedText" class="font-inherit text-inherit color-inherit whitespace-nowrap will-change-opacity">
        {{ displayedText }}
      </span>
    </Transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity v-bind("duration + 'ms'") ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
